/**
 * BirthdaysThisMonth — teacher-only dashboard widget.
 * Pulls student birthdays from enrollment DOBs (via /academy/calendar/birthdays/this-month)
 * and shows them as a friendly reminder card. Silent if empty or the call 401s.
 */
import React, { useEffect, useState } from 'react';
import { Cake, Loader2 } from 'lucide-react';
import api from '../services/api';

const authHeaders = () => ({ Authorization: `Bearer ${localStorage.getItem('academy_token') || ''}` });

const monthName = (iso) => {
  try {
    const [, m] = iso.split('-');
    return new Date(2000, Number(m) - 1, 1).toLocaleString('en-US', { month: 'long' });
  } catch { return ''; }
};
const dayOfMonth = (iso) => Number((iso || '').slice(8, 10)) || 0;
const todayISO = () => new Date().toISOString().slice(0, 10);

const BirthdaysThisMonth = () => {
  const [rows, setRows] = useState([]);
  const [month, setMonth] = useState('');
  const [loading, setLoading] = useState(true);
  const [errored, setErrored] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const res = await api.get('/academy/calendar/birthdays/this-month', { headers: authHeaders() });
        setRows(res.data.birthdays || []);
        setMonth(res.data.month || '');
      } catch {
        setErrored(true);
      } finally { setLoading(false); }
    })();
  }, []);

  if (errored) return null; // not a teacher/admin — silent
  if (loading) {
    return (
      <div className="rounded-xl border border-pink-500/30 bg-pink-500/5 p-4">
        <div className="text-[10px] uppercase tracking-[0.3em] font-black text-pink-400 inline-flex items-center gap-2">
          <Cake className="w-3.5 h-3.5" /> Birthdays This Month
        </div>
        <div className="mt-2 text-xs text-zinc-500 inline-flex items-center gap-2">
          <Loader2 className="w-3.5 h-3.5 animate-spin" /> Loading…
        </div>
      </div>
    );
  }

  const today = todayISO();
  const upcoming = rows.filter(r => r.date >= today);
  const past = rows.filter(r => r.date < today);

  return (
    <div className="rounded-xl border border-pink-500/30 bg-gradient-to-br from-pink-500/10 to-fuchsia-500/5 p-5" data-testid="birthdays-this-month">
      <div className="flex items-center justify-between">
        <div className="text-[10px] uppercase tracking-[0.3em] font-black text-pink-400 inline-flex items-center gap-2">
          <Cake className="w-3.5 h-3.5" /> Birthdays · {monthName(month)}
        </div>
        <div className="text-[10px] text-pink-400/70 font-bold">{rows.length} this month</div>
      </div>

      {rows.length === 0 && (
        <div className="mt-3 text-xs text-zinc-500 italic">No student birthdays this month.</div>
      )}

      {upcoming.length > 0 && (
        <div className="mt-3">
          <div className="text-[10px] uppercase tracking-widest text-pink-300 font-bold mb-2">Coming Up</div>
          <ul className="space-y-1.5">
            {upcoming.map((r) => (
              <li key={r.id} className="flex items-center gap-3 text-sm" data-testid={`birthday-row-${r.id}`}>
                <div className="w-10 h-10 rounded-lg bg-pink-500/20 border border-pink-500/40 flex flex-col items-center justify-center font-black text-pink-300">
                  <span className="text-[8px] uppercase leading-none tracking-wider">{monthName(r.date).slice(0, 3)}</span>
                  <span className="text-sm leading-none">{dayOfMonth(r.date)}</span>
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-white font-bold truncate">{r.student_name}</div>
                  <div className="text-[11px] text-zinc-400">
                    Turns {r.title?.match(/turns (\d+)/i)?.[1] || ''}{r.grade ? ` · Grade ${r.grade}` : ''}
                  </div>
                </div>
              </li>
            ))}
          </ul>
        </div>
      )}

      {past.length > 0 && (
        <details className="mt-3">
          <summary className="text-[10px] uppercase tracking-widest text-zinc-500 font-bold cursor-pointer">
            Already Celebrated ({past.length})
          </summary>
          <ul className="mt-2 space-y-1">
            {past.map((r) => (
              <li key={r.id} className="text-xs text-zinc-500">
                {monthName(r.date).slice(0,3)} {dayOfMonth(r.date)} · {r.student_name}
              </li>
            ))}
          </ul>
        </details>
      )}
    </div>
  );
};

export default BirthdaysThisMonth;
